import { ApiResponse } from '@tinyrewards/shared';

const BASE_URL = '';

/**
 * Where an expired session lands. "/" is the homepage; for a signed-out visitor
 * ProtectedRoute (client/src/App.tsx) forwards it to /login, which is the
 * hero page carrying the "Register / Log In" panel.
 */
const LOGIN_LANDING = '/';

/**
 * A 401 means two different things depending on whether we thought we were
 * signed in, and only one of them is a session expiring:
 *
 *  • Never signed in — an expected answer that callers rely on. ClaimQRPage is a
 *    public route that calls the gated /api/badge-access/by-code, the public
 *    marketing pages mount while anonymous, and /auth/me 401s for every visitor
 *    who isn't logged in. Redirecting any of those would hijack the page (and
 *    for a QR claim, throw away the code in the URL).
 *  • Signed in a moment ago — the session died underneath the user. The server's
 *    "Authentication required" is useless to them on a page that can no longer
 *    load, so we send them to the homepage to sign in again instead of
 *    surfacing it.
 *
 * AuthProvider owns the flag because it is the only thing that knows.
 */
let sessionActive = false;
let redirecting = false;

export function setSessionActive(active: boolean): void {
    sessionActive = active;
    if (active) redirecting = false;
}

function handleSessionExpired(): void {
    // Stop the next in-flight 401 from redirecting again — a page typically
    // fires several calls at once and they will all come back 401.
    sessionActive = false;
    if (redirecting || typeof window === 'undefined') return;
    redirecting = true;
    // A full document load, not a router navigation: it drops every piece of
    // stale in-memory auth state, and this module has no access to the router.
    window.location.assign(LOGIN_LANDING);
}

/**
 * Typed fetch wrapper for JSON API calls.
 * All requests include credentials (session cookie).
 */
async function request<T>(
    method: string,
    path: string,
    body?: unknown,
): Promise<ApiResponse<T>> {
    let res: Response;
    try {
        res = await fetch(`${BASE_URL}${path}`, {
            method,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch {
        return { success: false, message: 'Unable to reach the server. Please try again.' } as ApiResponse<T>;
    }

    // The auth router is excluded: its 401s are answers, not expiries — /auth/login
    // 401s on bad credentials and /auth/me 401s when anonymous, and those callers
    // handle it themselves. Everything else the SPA calls sits behind
    // requireVerifiedEmail, which only 401s once the session is gone.
    if (res.status === 401 && sessionActive && !path.startsWith('/auth/')) {
        handleSessionExpired();
        // Empty message so nothing flashes up before the navigation lands:
        // callers render `{error && <Alert>}` / `open={!!snackbar}`, and `??`
        // would let a fallback string through if this were undefined.
        return { success: false, message: '' } as ApiResponse<T>;
    }

    const contentType = res.headers.get('Content-Type') ?? '';
    if (!contentType.includes('application/json')) {
        return { success: false, message: `Server error (${res.status})` } as ApiResponse<T>;
    }

    try {
        const json = await res.json();
        return json as ApiResponse<T>;
    } catch {
        return { success: false, message: 'Invalid response from server.' } as ApiResponse<T>;
    }
}

export const apiGet = <T>(path: string) => request<T>('GET', path);
export const apiPost = <T>(path: string, body: unknown) => request<T>('POST', path, body);
export const apiPut = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
export const apiPatch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, body);
export const apiDelete = <T>(path: string) => request<T>('DELETE', path);
